import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GitCompare, FileText, CheckCircle, Clock, AlertTriangle, 
  ChevronDown, ChevronRight, Plus, ArrowRight, Shield, Layers,
  Download, RefreshCw, Info, Calendar, User, X, Send, Check,
  XCircle, Lock, Unlock, FolderKanban, Filter, Eye, Rocket
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { adaptBaselinesResponse, normalizeBaseline } from '../utils/dataAdapters';
import BaselineDiffViewer from '../components/BaselineDiffViewer';
import WBSTree from '../components/WBSTree';
import ExportDocumentsDropdown from '../components/ExportDocumentsDropdown';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';

// ==========================================
// CONSTANTS
// ==========================================

const STATUS_CONFIG = {
  draft: {
    label: 'Draft',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Unlock,
    canEdit: true,
    canSubmit: true,
    canApprove: false,
  },
  submitted: {
    label: 'Submitted',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Send,
    canEdit: false,
    canSubmit: false,
    canApprove: true,
  },
  approved: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
    canEdit: false,
    canSubmit: false,
    canApprove: false,
    isLocked: true,
  },
  superseded: {
    label: 'Superseded',
    color: 'bg-gray-100 text-gray-500 border-gray-200',
    icon: XCircle,
    canEdit: false,
    canSubmit: false,
    canApprove: false,
    isLocked: true,
  },
};

// ==========================================
// BASELINE CARD COMPONENT
// ==========================================

const BaselineCard = ({
  baseline,
  isSelected,
  onSelect,
  onView,
  onSubmit,
  onApprove,
  onReject,
  onCreateProject,
  userId,
}) => {
  const config = STATUS_CONFIG[baseline.status] || STATUS_CONFIG.draft;
  const StatusIcon = config.icon;
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className={`p-4 rounded-lg border cursor-pointer transition-all ${
        isSelected 
          ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)]/5 ring-2 ring-[var(--clr-brand-primary)]/20' 
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      onClick={() => onSelect(baseline)}
      data-testid={`baseline-card-${baseline.version}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">
            {baseline.name || `Baseline V${baseline.version}`}
          </span>
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${config.color}`}>
            <StatusIcon className="w-3 h-3" />
            {config.label}
          </span>
          {config.isLocked && (
            <Lock className="w-3.5 h-3.5 text-gray-400" title="Locked" />
          )}
        </div>
        
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(false);
                }} 
              />
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(baseline);
                    setShowActions(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
                {config.canSubmit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSubmit(baseline);
                      setShowActions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-blue-600"
                  >
                    <Send className="w-4 h-4" />
                    Submit for Approval
                  </button>
                )}
                {config.canApprove && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(baseline);
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReject(baseline);
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
                {baseline.status === 'approved' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateProject(baseline);
                      setShowActions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-purple-600 border-t border-gray-100 mt-1"
                    data-testid="create-project-from-baseline-btn"
                  >
                    <Rocket className="w-4 h-4" />
                    Create Delivery Project
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
        <span className="flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" />
          {baseline.requirement_count || 0} requirements
        </span>
        <span className="flex items-center gap-1">
          <FolderKanban className="w-3.5 h-3.5" />
          {baseline.wbs_node_count || 0} WBS nodes
        </span>
        {baseline.quality_score > 0 && (
          <span className={`flex items-center gap-1 ${
            baseline.quality_score >= 80 ? 'text-green-600' :
            baseline.quality_score >= 60 ? 'text-amber-600' : 'text-red-600'
          }`}>
            <CheckCircle className="w-3.5 h-3.5" />
            {baseline.quality_score}% quality
          </span>
        )}
      </div>

      {/* Notes */}
      {baseline.notes && (
        <p className="text-sm text-gray-500 mb-2 line-clamp-2">{baseline.notes}</p>
      )}

      {/* Metadata */}
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {new Date(baseline.created_at).toLocaleDateString()}
        </span>
        {baseline.approved_at && (
          <span className="flex items-center gap-1 text-green-600">
            <Check className="w-3 h-3" />
            Approved {new Date(baseline.approved_at).toLocaleDateString()}
          </span>
        )}
        {baseline.rejection_reason && (
          <span className="flex items-center gap-1 text-red-500" title={baseline.rejection_reason}>
            <AlertTriangle className="w-3 h-3" />
            Rejected
          </span>
        )}
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const EnhancedScopeBaselinesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant?.id || user?.tenant_id;
  const userId = user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [baselines, setBaselines] = useState([]);
  
  // Selection state
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  const [compareFrom, setCompareFrom] = useState(null);
  const [compareTo, setCompareTo] = useState(null);
  
  // View state
  const [viewMode, setViewMode] = useState('list'); // list, detail, compare
  const [showWBS, setShowWBS] = useState(true);
  
  // Modal state
  const [showDiffViewer, setShowDiffViewer] = useState(false);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);

  // Load projects
  useEffect(() => {
    if (tenantId) {
      loadProjects();
    }
  }, [tenantId]);

  // Load baselines when project changes
  useEffect(() => {
    if (selectedProject) {
      loadBaselines();
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const res = await api.getScopingProjects(tenantId);
      const projectList = res.data.projects || [];
      setProjects(projectList);

      const projectIdParam = searchParams.get('project');
      if (projectIdParam) {
        const project = projectList.find(p => p.id === projectIdParam);
        if (project) setSelectedProject(project);
      } else if (projectList.length > 0) {
        setSelectedProject(projectList[0]);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadBaselines = async () => {
    if (!selectedProject) return;
    try {
      // Use listScopingBaselines which calls /scoping/baselines - the correct endpoint for scope baselines
      const res = await api.listScopingBaselines({ tenant_id: tenantId, project_id: selectedProject.id });
      const adapted = adaptBaselinesResponse(res.data);
      setBaselines(adapted.baselines || []);
      
      // Auto-select the latest approved or draft baseline
      const approved = adapted.baselines.find(b => b.status === 'approved');
      const draft = adapted.baselines.find(b => b.status === 'draft');
      setSelectedBaseline(approved || draft || adapted.baselines[0] || null);
    } catch (err) {
      console.error('Failed to load baselines:', err);
    }
  };

  // Actions
  const handleCreateBaseline = async () => {
    try {
      await api.createBaseline(tenantId, userId, {
        project_id: selectedProject.id,
        notes: 'New baseline',
      });
      toast.success('Baseline created');
      loadBaselines();
    } catch (err) {
      console.error('Failed to create baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to create baseline');
    }
  };

  const handleGenerateWBS = async () => {
    if (!selectedBaseline) return;
    try {
      await api.generateWBS(tenantId, userId, {
        project_id: selectedProject.id,
        baseline_id: selectedBaseline.id,
        include_phases: true,
      });
      toast.success('WBS generated successfully');
      loadBaselines();
    } catch (err) {
      console.error('Failed to generate WBS:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate WBS');
    }
  };

  const handleSubmitBaseline = async (baseline) => {
    const notes = window.prompt('Add submission notes (optional):');
    try {
      await api.submitBaseline(baseline.id, tenantId, userId, notes);
      toast.success('Baseline submitted for approval');
      loadBaselines();
    } catch (err) {
      console.error('Failed to submit baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit baseline');
    }
  };

  const handleApproveBaseline = async (baseline) => {
    const notes = window.prompt('Add approval notes (optional):');
    try {
      await api.approveBaseline(baseline.id, tenantId, userId, notes);
      toast.success('Baseline approved and locked');
      loadBaselines();
    } catch (err) {
      console.error('Failed to approve baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve baseline');
    }
  };

  const handleRejectBaseline = async (baseline) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      await api.rejectBaseline(baseline.id, tenantId, userId, reason);
      toast.success('Baseline rejected');
      loadBaselines();
    } catch (err) {
      console.error('Failed to reject baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to reject baseline');
    }
  };

  const handleCompare = () => {
    if (!compareFrom || !compareTo) {
      toast.error('Select two baselines to compare');
      return;
    }
    if (compareFrom.id === compareTo.id) {
      toast.error('Select different baselines to compare');
      return;
    }
    setShowDiffViewer(true);
  };

  const handleCreateChangeRequest = () => {
    setShowChangeRequestModal(true);
  };

  const handleCreateProjectFromBaseline = async (baseline) => {
    if (baseline.status !== 'approved') {
      toast.error('Only approved baselines can be converted to projects');
      return;
    }
    
    const projectName = window.prompt(
      'Enter project name:',
      `${selectedProject?.name || 'Baseline'} - Delivery`
    );
    
    if (!projectName) return;
    
    try {
      const res = await api.createProjectFromBaseline(baseline.id, tenantId, userId, projectName);
      toast.success(res.data.message || 'Delivery project created successfully');
      
      // Navigate to the new project dashboard
      navigate(`/project/${res.data.project_id}`);
    } catch (err) {
      console.error('Failed to create project from baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to create project from baseline');
    }
  };

  // Stats
  const stats = useMemo(() => {
    const approved = baselines.filter(b => b.status === 'approved').length;
    const draft = baselines.filter(b => b.status === 'draft').length;
    const submitted = baselines.filter(b => b.status === 'submitted').length;
    return { total: baselines.length, approved, draft, submitted };
  }, [baselines]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-8 bg-gray-200 rounded w-48 animate-pulse mb-6" />
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
        <SkeletonTable rows={5} columns={5} />
      </div>
    );
  }

  return (
    <PageErrorBoundary pageName="Scope Baselines">
      <div className="p-6 max-w-7xl mx-auto" data-testid="scope-baselines-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scope Baselines</h1>
            <p className="text-gray-500 text-sm">
              Manage baseline versions, approvals, and track changes
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProject?.id || ''}
              onChange={(e) => {
                const project = projects.find(p => p.id === e.target.value);
                setSelectedProject(project);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="project-selector"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {selectedProject && selectedBaseline && (
              <ExportDocumentsDropdown
                projectId={selectedProject.id}
                tenantId={tenantId}
                baselineId={selectedBaseline.id}
                buttonText="Export"
              />
            )}
            <button
              onClick={() => loadBaselines()}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={handleCreateBaseline}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
              data-testid="create-baseline-btn"
            >
              <Plus className="w-4 h-4" />
              New Baseline
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <WidgetErrorBoundary widgetName="Baseline Stats">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1">Total Baselines</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Approved
              </div>
              <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <Send className="w-4 h-4 text-blue-500" />
                Pending Approval
              </div>
              <div className="text-2xl font-bold text-blue-600">{stats.submitted}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <Unlock className="w-4 h-4 text-amber-500" />
                Draft
              </div>
              <div className="text-2xl font-bold text-amber-600">{stats.draft}</div>
            </div>
          </div>
        </WidgetErrorBoundary>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Baselines List */}
          <div className="col-span-1">
            <WidgetErrorBoundary widgetName="Baselines List">
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Baselines</h3>
                </div>
                
                {baselines.length === 0 ? (
                  <div className="p-6">
                    <EmptyState
                      icon={GitCompare}
                      title="No baselines"
                      description="Create your first baseline to start tracking scope."
                      action={{
                        label: "Create Baseline",
                        onClick: handleCreateBaseline,
                      }}
                    />
                  </div>
                ) : (
                  <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                    {baselines.map(baseline => (
                      <BaselineCard
                        key={baseline.id}
                        baseline={baseline}
                        isSelected={selectedBaseline?.id === baseline.id}
                        onSelect={setSelectedBaseline}
                        onView={setSelectedBaseline}
                        onSubmit={handleSubmitBaseline}
                        onApprove={handleApproveBaseline}
                        onReject={handleRejectBaseline}
                        onCreateProject={handleCreateProjectFromBaseline}
                        userId={userId}
                      />
                    ))}
                  </div>
                )}
              </div>
            </WidgetErrorBoundary>

            {/* Compare Section */}
            <WidgetErrorBoundary widgetName="Baseline Compare">
              <div className="bg-white rounded-lg border mt-4">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <GitCompare className="w-4 h-4" />
                    Compare Baselines
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">From (older)</label>
                    <select
                      value={compareFrom?.id || ''}
                      onChange={(e) => setCompareFrom(baselines.find(b => b.id === e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                      data-testid="compare-from"
                    >
                      <option value="">Select baseline...</option>
                      {baselines.map(b => (
                        <option key={b.id} value={b.id}>V{b.version} ({b.status})</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-center">
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">To (newer)</label>
                    <select
                      value={compareTo?.id || ''}
                      onChange={(e) => setCompareTo(baselines.find(b => b.id === e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                      data-testid="compare-to"
                    >
                      <option value="">Select baseline...</option>
                      {baselines.map(b => (
                        <option key={b.id} value={b.id}>V{b.version} ({b.status})</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleCompare}
                    disabled={!compareFrom || !compareTo}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    data-testid="compare-btn"
                  >
                    <GitCompare className="w-4 h-4" />
                    Compare
                  </button>
                </div>
              </div>
            </WidgetErrorBoundary>
          </div>

          {/* WBS View */}
          <div className="col-span-2">
            <WidgetErrorBoundary widgetName="WBS View">
              <div className="bg-white rounded-lg border h-full">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FolderKanban className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                    {selectedBaseline 
                      ? `WBS - ${selectedBaseline.name || `V${selectedBaseline.version}`}` 
                      : 'Work Breakdown Structure'}
                    {selectedBaseline?.status === 'approved' && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        <Lock className="w-3 h-3" />
                        Locked
                      </span>
                    )}
                  </h3>
                  {selectedBaseline && selectedBaseline.status === 'draft' && (
                    <button
                      onClick={handleGenerateWBS}
                      className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                      data-testid="generate-wbs-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Generate WBS
                    </button>
                  )}
                </div>
                <div className="p-4">
                  {selectedBaseline ? (
                    <WBSTree
                      projectId={selectedProject?.id}
                      tenantId={tenantId}
                      baselineId={selectedBaseline.id}
                      showHeader={false}
                    />
                  ) : (
                    <EmptyState
                      icon={FolderKanban}
                      title="Select a baseline"
                      description="Select a baseline from the list to view its WBS structure."
                    />
                  )}
                </div>
              </div>
            </WidgetErrorBoundary>
          </div>
        </div>

        {/* Diff Viewer Modal */}
        {showDiffViewer && compareFrom && compareTo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowDiffViewer(false)} />
            <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <BaselineDiffViewer
                fromBaselineId={compareFrom.id}
                toBaselineId={compareTo.id}
                tenantId={tenantId}
                onClose={() => setShowDiffViewer(false)}
                onCreateChangeRequest={handleCreateChangeRequest}
              />
            </div>
          </div>
        )}

        {/* Change Request Modal (Placeholder) */}
        {showChangeRequestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowChangeRequestModal(false)} />
            <div className="relative z-10 bg-white rounded-xl shadow-2xl p-6 max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <Info className="w-8 h-8 text-blue-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">Change Request Module</h3>
                  <p className="text-sm text-gray-500">Coming in next phase</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                The Change Request module will allow you to formally document and track scope changes 
                between baseline versions. This feature is planned for the next development phase.
              </p>
              <button
                onClick={() => setShowChangeRequestModal(false)}
                className="w-full px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </div>
    </PageErrorBoundary>
  );
};

export default EnhancedScopeBaselinesPage;
